<script>
  // Import Components
  import { LayerCake, Svg, Html, flatten } from 'layercake';
  import { stack } from 'd3-shape';
  import { scaleOrdinal } from 'd3-scale';
  import { format, precisionFixed } from 'd3-format';
  import { timeParse, timeFormat } from 'd3-time-format';
  import AxisX from './components/AxisX.svelte';
  import AxisY from './components/AxisY.svelte';
  import AreaStacked from './components/AreaStacked.svelte';
  import SharedTooltip from './components/SharedTooltip.svelte';

  // Import Data
  import per_pupil_infl_adj_supp from './data/per_pupil_infl_adj_supp_function.js';
  import per_pupil_supp from './data/per_pupil_supp_function.js';
  import total_infl_adj_supp from './data/total_infl_adj_supp_function.js';
  import total_supp from './data/total_supp_function.js';
  import percent_supp from './data/percent_supp_function.js';
  import stateIndex from './data/state_index.js';
  import outputIndex from './data/output_index.js';

  const xKey = 'year';
  const yKey = [0, 1];
  const zKey = 'key';

  const parseDate = timeParse('%Y-%m-%d');
  const seriesColors = ['rgb(237, 112, 67, 0.85)', 'rgb(40, 121, 203, 0.85)', 'rgb(46, 55, 69, 0.85)', 'rgb(246, 185, 65, 0.85)', 'rgb(166, 159, 161, 0.85)', 'rgb(102, 153, 204, 0.85)', 'rgb(102, 153, 0, 0.85)'];

  let arrayIndex = 0;

  $: output = 0;

  $: data = per_pupil_infl_adj_supp[arrayIndex];

  $: { 
    output = output;
    
    if(output == 0) {
      data = per_pupil_infl_adj_supp[arrayIndex]
    } else if (output == 1) {
      data = total_infl_adj_supp[arrayIndex]
    } else if (output == 2) {
      data = per_pupil_supp[arrayIndex]
    } else if (output == 3) {
      data = total_supp[arrayIndex]
    } else {
      data = percent_supp[arrayIndex]
    }
};

  $: seriesNames = Object.keys(data[0]).filter(d => d !== xKey);

  $: {
    data.forEach(d => {
    d[xKey] = typeof d[xKey] === 'string' ? parseDate(d[xKey]) : d[xKey];
    seriesNames.forEach(name => {
      d[name] = +d[name];
    });
  });
}


  /* --------------------------------------------
   * Create a stacked data structure
   */
  $: stackData = stack()
    .keys(seriesNames);

  $: series = stackData(data);

  const formatTickX = timeFormat("%Y")
  const formatTickXAxis = timeFormat("'%y")
  $: formatTickY = d => format(`$,.2s`)(d).replace(/G/, " B").replace(/M/, " M");

  $: {
    output = output;
    
    if(output == 4) {
      formatTickY = d => format(`.0%`)(d);
    } else if (output == 0) {
      formatTickY = d => format(`$,.0f`)(d);
    } else if (output == 2) {
      formatTickY = d => format(`$,.0f`)(d);
    } else {
      formatTickY = d => format(`$,.2s`)(d).replace(/G/, " B").replace(/M/, " M");
    }
  }


  $: filterDomain = Object.keys(data[0]).filter(d => d !== "year");

  Object.filter = (obj, predicate) => 
    Object.keys(obj)
          .filter( key => predicate(obj[key]) )
          .reduce( (res, key) => (res[key] = obj[key], res), {} );

  

  let nameLabel = 'United States';
  $: {
    nameLabel = stateIndex[arrayIndex].State
  }
  let outputLabel = 'blank';
  $: {
    outputLabel = outputIndex[output].Output
  }
  let inflationAdjusted = "(Inflation Adjusted)";
  $: {
    if(output == 0) {
      inflationAdjusted = "(Inflation Adjusted)"
    } else if (output == 1) {
      inflationAdjusted = "(Inflation Adjusted)"
    } else if (output == 2) {
      inflationAdjusted = ""
    } else if (output == 3) {
      inflationAdjusted = ""
    } else {
      inflationAdjusted = ""
    }
  }
</script>


<style>
  .chart-container {
    width: 94%;
    margin-left: 5%;
    margin-right: 1%;
    height: 315px;
  }
  
  .column {
    float: left;
    width: 50%;
  }

  .row:after {
    content: "";
    display: table;
    clear: both;
  }

  h3 {
    font-family: 'Open Sans', sans-serif;
  }

  .footnote {
    font-size: 11px;
  }

  a {
    color: #f63;
  }

  .legend {
    margin-left: 10px;
    border-radius: 50%; 
    display: inline-block; 
    width: 10px; 
    height: 10px;
  }

  .legend-container {
    margin: -5px 0 10px 0;
    font-family: 'Open Sans', sans-serif;
    text-align: center;
    font-size: 14px;
  }
</style>

<div class = "row">
  <div class="column">
      <select bind:value={arrayIndex}>
        <option value={0}>United States</option>
        <option value={1}>Alabama</option>
        <option value={2}>Alaska</option>
        <option value={3}>Arizona</option>
        <option value={4}>Arkansas</option>
        <option value={5}>California</option>
        <option value={6}>Colorado</option>
        <option value={7}>Connecticut</option>
        <option value={8}>Delaware</option>
        <option value={9}>District of Columbia</option>
        <option value={10}>Florida</option>
        <option value={11}>Georgia</option>
        <option value={12}>Hawaii</option>
        <option value={13}>Idaho</option>
        <option value={14}>Illinois</option>
        <option value={15}>Indiana</option>
        <option value={16}>Iowa</option>
        <option value={17}>Kansas</option>
        <option value={18}>Kentucky</option>
        <option value={19}>Louisiana</option>
        <option value={20}>Maine</option>
        <option value={21}>Maryland</option>
        <option value={22}>Massachusetts</option>
        <option value={23}>Michigan</option>
        <option value={24}>Minnesota</option>
        <option value={25}>Mississippi</option>
        <option value={26}>Missouri</option>
        <option value={27}>Montana</option>
        <option value={28}>Nebraska</option>
        <option value={29}>Nevada</option>
        <option value={30}>New Hampshire</option>
        <option value={31}>New Jersey</option>
        <option value={32}>New Mexico</option>
        <option value={33}>New York</option>
        <option value={34}>North Carolina</option>
        <option value={35}>North Dakota</option>
        <option value={36}>Ohio</option>
        <option value={37}>Oklahoma</option>
        <option value={38}>Oregon</option>
        <option value={39}>Pennsylvania</option>
        <option value={40}>Rhode Island</option>
        <option value={41}>South Carolina</option>
        <option value={42}>South Dakota</option>
        <option value={43}>Tennessee</option>
        <option value={44}>Texas</option>
        <option value={45}>Utah</option>
        <option value={46}>Vermont</option>
        <option value={47}>Virginia</option>
        <option value={48}>Washington</option>
        <option value={49}>West Virginia</option>
        <option value={50}>Wisconsin</option>
        <option value={51}>Wyoming</option>
      </select>
  </div>

  <div class="column" style="text-align: right">
    <select bind:value={output}>
      <option value={0}>Per Pupil (inflation adjusted)</option>
      <option value={1}>Total (inflation adjusted)</option>
      <option value={2}>Per Pupil</option>
      <option value={3}>Total</option>
      <option value={4}>Percent</option>
    </select>
  </div>
</div>

<h3>K-12 Support Services Spending <span style = "font-size: 16px; font-style: italic;">(by function)</span></h3>



<!-- console.log(series.filter(el => { return el.key == "apples"})); -->


<div class="chart-container">

  <div class="legend-container">
    <div class="legend" style="background-color: rgb(237, 112, 67)"></div>  
    Operation & Maintenance
    <div class="legend" style="background-color: rgb(40, 121, 203)"></div>  
    Pupil Support Services
    <div class="legend" style="background-color: rgb(46, 55, 69)"></div>
    School Admin
    <br>
    <div class="legend" style="background-color: rgb(246, 185, 65)"></div>
    Instructional Staff
    <div class="legend" style="background-color: rgb(166, 159, 161)"></div>
    Pupil Transportation
    <div class="legend" style="background-color: rgb(102, 153, 204)"></div>
    Other
    <div class="legend" style="background-color: rgb(102, 153, 0)"></div>
    General Admin
  </div>

  <LayerCake
    padding={{ top: 0, right: 0, bottom: 20, left: 17 }}
    x={d => d.data[xKey]}
    y={yKey}
    z={zKey}
    zScale={scaleOrdinal()}
    zDomain={filterDomain}
    zRange={seriesColors}
    flatData={flatten(series)}
    data={series}
  >
    <Svg>
      <AxisX
        snapTicks={true}
        ticks={data.map(d => d.year).reverse()}
        formatTick={formatTickXAxis}
      />
      <AxisY
        formatTick={formatTickY}
      />
      <AreaStacked
      />
    </Svg>

    <Html>
      <SharedTooltip
        formatTitle={formatTickX}
        dataset={data}
        seriesColors = {seriesColors}
      />
    </Html>
  </LayerCake>
  <span class="footnote">Source: <a href="https://reason.org/topics/education/"  target="_blank">Reason Foundation</a> &#183; <a href="https://ss2-support-services-function.vercel.app/"  target="_blank">Link for Embed</a> </span>
</div>
